/**
 * The decisions the POI sheet makes about what to show.
 *
 * The sheet is one component serving every kind of POI — a summit, a water
 * source, a hostel, a bus stop — and each of them arrives with a different
 * subset of the same fields. Some have a Google place behind them with photos
 * and reviews; most have nothing but a name and a coordinate. Keeping those
 * choices here, rather than as `v-if` chains in the template, is what makes
 * them testable.
 */

import { i18n } from '../../i18n'

export interface SerpReview {
  author: string
  rating: number | null
  text: string
  when: string
}

interface RawSerpReview {
  username?: string
  author_name?: string
  rating?: number
  description?: string
  text?: string
  relative_time_description?: string
  date?: string
}

export interface SerpImage {
  thumbnail?: string
  serpapi_thumbnail?: string
}

/**
 * The photo collage: one large image, then up to two stacked beside it.
 *
 * Three is the shape the design draws. Fewer is common and has to still look
 * deliberate, so the caller is told how many it actually got rather than being
 * handed padding to render.
 */
export function photoCollage (images: SerpImage[] | null | undefined, limit = 3): string[] {
  return (images || []).
    map((image) => image?.serpapi_thumbnail || image?.thumbnail || '').
    filter((url) => url.length > 0).
    slice(0, Math.max(limit, 0))
}

/**
 * Normalises the two review shapes the proxy can return.
 *
 * SerpApi moved reviews into `user_reviews.most_relevant`, but older cached
 * payloads still carry a flat `reviews` array — the client passes both through
 * untouched, so the sheet has to cope with either. Reviews with no text are
 * dropped: a bare star rating adds nothing under a rating the header already
 * shows.
 */
export function normalisePoiReviews (
  place: { user_reviews?: { most_relevant?: RawSerpReview[] }, reviews?: RawSerpReview[] } | null | undefined,
  limit = 5
): SerpReview[] {
  const raw = place?.user_reviews?.most_relevant || place?.reviews || []

  return raw.
    map((review) => ({
      author: (review.username || review.author_name || i18n.global.t('utils.poi_sheet.anonymous')).trim(),
      rating: typeof review.rating === 'number' ? review.rating : null,
      text: (review.description || review.text || '').trim(),
      when: (review.relative_time_description || review.date || '').trim()
    })).
    filter((review) => review.text.length > 0).
    slice(0, Math.max(limit, 0))
}

/**
 * A description as plain text.
 *
 * A POI description can be an ActionText field, so it may arrive as rendered
 * markup — interpolating that prints the tags, and `v-html`-ing server HTML
 * into the sheet buys nothing when the panel wants one paragraph of prose.
 * DOMParser is inert: unlike innerHTML nothing in the parsed document runs or
 * loads, so an `<img onerror>` smuggled into an OSM tag stays a no-op.
 */
export function plainText (value: string | null | undefined): string {
  const raw = (value || '').trim()
  if (!raw || !raw.includes('<')) return raw

  const text = new DOMParser().parseFromString(raw, 'text/html').body.textContent || ''
  return text.replace(/\s+/g, ' ').trim()
}

/** Whether a description is long enough to be worth a "Show more". */
export function needsShowMore (text: string | null | undefined, limit = 180): boolean {
  return (text || '').trim().length > limit
}

/**
 * The description as first shown, cut at a word boundary so it does not end
 * mid-word before the ellipsis.
 */
export function truncateAtWord (text: string | null | undefined, limit = 180): string {
  const raw = (text || '').trim()
  if (raw.length <= limit) return raw

  const cut = raw.slice(0, limit)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/**
 * A website URL fit to render.
 *
 * POI rows carry a fair amount of junk here — a bare "http://" from a truncated
 * OSM tag is the common one — and scheme-less hosts, which need a scheme before
 * they will resolve.
 */
export function websiteUrl (value: string | null | undefined): string | null {
  const raw = (value || '').trim()
  if (!raw || /^https?:\/\/\/?$/i.test(raw)) return null
  if (/^https?:\/\//i.test(raw)) return raw
  if (!/^[\w-]+(\.[\w-]+)+/.test(raw)) return null
  return `https://${raw}`
}

/** The host, for showing a link as "greendragon.com" rather than the full URL. */
export function websiteLabel (value: string | null | undefined): string {
  const url = websiteUrl(value)
  if (!url) return ''

  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/**
 * "1.2 km away", in the viewer's units.
 *
 * Only shown when the host page already knows where the reader is — the sheet
 * never asks for geolocation on its own, so this returns empty rather than
 * prompting.
 */
export function formatDistanceAway (meters: number | null | undefined, metric = true): string {
  if (typeof meters !== 'number' || Number.isNaN(meters) || meters < 0) return ''

  if (metric) {
    return meters < 1000
      ? i18n.global.t('utils.poi_sheet.meters_away', { distance: Math.round(meters) })
      : i18n.global.t('utils.poi_sheet.km_away', { distance: (meters / 1000).toFixed(1) })
  }

  const miles = meters / 1609.344
  return miles < 0.1
    ? i18n.global.t('utils.poi_sheet.feet_away', { distance: Math.round(meters * 3.28084) })
    : i18n.global.t('utils.poi_sheet.miles_away', { distance: miles.toFixed(1) })
}

/** "213 m elevation", or empty when the POI carries no height. */
export function formatElevation (meters: number | null | undefined): string {
  if (typeof meters !== 'number' || Number.isNaN(meters)) return ''
  return i18n.global.t('utils.poi_sheet.meters_elevation', { elevation: Math.round(meters).toLocaleString('en-GB') })
}

/**
 * The rating line: a rounded star count plus the exact figure.
 *
 * Returns null below 1 star so the header does not render an empty row of grey
 * stars for a place Google has no opinion about.
 */
export function ratingSummary (
  rating: number | null | undefined,
  total: number | null | undefined
): { stars: number, label: string } | null {
  if (typeof rating !== 'number' || Number.isNaN(rating) || rating <= 0) return null

  const reviews = total || 0
  return {
    stars: Math.round(rating),
    label: reviews > 0 ? `${rating.toFixed(1)} (${reviews.toLocaleString('en-GB')})` : rating.toFixed(1)
  }
}
